// Why a creative cannot be edited right now.
//
// Since 0023 content is immutable per revision, and the server no longer
// refuses an edit of an approved or in-use creative: it saves the next version
// (`lib/creative-revision-policy.ts` says so before the click). What remains
// locked in place is an ARCHIVED creative — restore it first — and, in the
// campaign builder, every section of a published campaign, whose creative slot
// names the version it flies and never edits it. This module decides the first
// of those, in one tested place, so the drawer only renders it.

import type { Creative, CreativePin } from '@/api/types';

export type CreativeLockKind = 'archived';

export interface CreativeLock {
  kind: CreativeLockKind;
  /** Plain words: what is locked and what to do instead. */
  message: string;
}

type Lockable = Pick<Creative, 'status'> & { pinned_by?: CreativePin[] | null };

/** Why content edits are refused outright, or `null` when a save has a path. */
export function creativeEditLock(creative: Lockable): CreativeLock | null {
  if (creative.status === 'archived') {
    return {
      kind: 'archived',
      message: "Archived creatives can't be edited. Restore it to a draft first.",
    };
  }
  return null;
}

/** Archive is refused while any published campaign still uses the creative. */
export function creativeArchiveLock(
  creative: Lockable,
): { kind: 'pinned'; message: string } | null {
  const pins = (creative.pinned_by ?? []).filter((pin) => pin.campaign_status !== 'draft');
  const [first, ...rest] = pins;
  if (!first) return null;
  const others =
    rest.length === 0
      ? ''
      : rest.length === 1
        ? ' and 1 other campaign'
        : ` and ${rest.length} other campaigns`;
  return {
    kind: 'pinned',
    message: `${first.campaign_name}${others} still use${rest.length === 0 ? 's' : ''} this creative; archive is available once they end.`,
  };
}
